import { forwardRef } from "react";
import formatCI from "../../utils/formatCI";
import useIsMobile from "../../hooks/useIsMobile";

function fmtDate(date) {
  const d = String(date.getDate()).padStart(2, "0");
  const m = String(date.getMonth() + 1).padStart(2, "0");
  return `${d}/${m}/${date.getFullYear()}`;
}

function fmtDateFromISO(iso) {
  if (!iso) return "___________";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y}`;
}

const ActAditionalPreview = forwardRef(function ActAditionalPreview({ data }, ref) {
  const isMobile = useIsMobile();
  const today = fmtDate(new Date());
  const ciProprietarFmt = data.proprietarCI ? formatCI(data.proprietarCI) : "___ ___";
  const ciChiriasFmt = data.chiriasCI ? formatCI(data.chiriasCI) : "___ ___";

  const p = {
    fontSize: "14px",
    fontFamily: "'Georgia', 'Times New Roman', serif",
    color: "#3d3225",
    lineHeight: 1.8,
    margin: "0 0 12px 0",
    textAlign: "justify",
  };

  const bold = { fontWeight: 700, color: "#2d2418" };

  // Build dynamic articles
  const articles = [];
  let articleNum = 1;

  if (data.prelungire) {
    const durata = data.prelungireNedeterminata
      ? "pe perioadă nedeterminată"
      : `până la data de ${data.nouaDataIncetare ? fmtDateFromISO(data.nouaDataIncetare) : "___________"}`;
    articles.push({
      num: articleNum++,
      text: `Durata contractului de închiriere se prelungește ${durata}.`,
    });
  }

  if (data.modificareChirie) {
    const sumaVeche = data.chirieVeche || "___";
    const monedaVeche = data.chirieVecheMoneda || "EUR";
    const sumaNoua = data.chirieNoua || "___";
    const monedaNoua = data.chirieNouaMoneda || "EUR";
    const dataAplicare = data.dataAplicareChirie ? fmtDateFromISO(data.dataAplicareChirie) : "___________";
    articles.push({
      num: articleNum++,
      text: `Chiria lunară se modifică de la ${sumaVeche} ${monedaVeche} la ${sumaNoua} ${monedaNoua}, începând cu data de ${dataAplicare}.`,
    });
  }

  if (data.modificareClauze) {
    articles.push({
      num: articleNum++,
      text: data.descriereModificare || "___________________________",
    });
  }

  articles.push({
    num: articleNum++,
    text: `Prezentul act adițional va fi comunicat organelor fiscale competente (ANAF) conform legislației în vigoare.`,
  });

  articles.push({
    num: articleNum,
    text: `Celelalte clauze ale Contractului de Închiriere nr. ${data.numarContract || "___"} din data de ${fmtDateFromISO(data.dataContract)} rămân nemodificate și în vigoare.`,
  });

  return (
    <div
      ref={ref}
      style={{
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: isMobile ? "20px 16px" : "48px 52px",
        color: "#2d2418",
        border: "1px solid #e8e0d4",
        maxHeight: "600px",
        overflow: "auto",
        boxShadow: "0 2px 16px rgba(0,0,0,0.06)",
        position: "relative",
      }}
    >
      {/* Decorative top border */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, height: "4px",
        background: "linear-gradient(90deg, #c45d3e, #a84832, #c45d3e)",
        borderRadius: "12px 12px 0 0",
      }} />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "32px" }}>
        <h2 style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "22px",
          fontWeight: 800,
          color: "#2d2418",
          margin: "0 0 4px 0",
          letterSpacing: "0.06em",
          textTransform: "uppercase",
        }}>
          Act Adițional Nr. {data.numarContract || "____"}
        </h2>
        <p style={{
          fontFamily: "'Playfair Display', serif",
          fontSize: "16px",
          fontWeight: 700,
          color: "#4a3f35",
          margin: "4px 0 0 0",
        }}>
          la Contractul de Închiriere nr. {data.numarContract || "___"} din data de {fmtDateFromISO(data.dataContract)}
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "2px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* Date */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "20px" }}>
        <p style={p}>
          Încheiat astăzi, <span style={bold}>{today}</span>, între:
        </p>
      </div>

      {/* Proprietar */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "20px" }}>
        <p style={p}>
          <span style={bold}>1. Proprietarul:</span>
        </p>
        <p style={{ ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" }}>
          Nume: <span style={bold}>{data.proprietarNume || "________________________"}</span>
        </p>
        <p style={{ ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" }}>
          CNP: <span style={bold}>{data.proprietarCNP || "________________________"}</span>
        </p>
        <p style={{ ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" }}>
          CI seria <span style={bold}>{ciProprietarFmt}</span>
        </p>
        <p style={{ ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" }}>
          Domiciliul: <span style={bold}>{data.proprietarAdresa || "________________________"}</span>
        </p>
      </div>

      {/* si */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "20px" }}>
        <p style={{ ...p, textAlign: "center", fontStyle: "italic", color: "#8a7e70" }}>și</p>
      </div>

      {/* Chirias */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "24px" }}>
        <p style={p}>
          <span style={bold}>2. Chiriașul:</span>
        </p>
        <p style={{ ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" }}>
          Nume: <span style={bold}>{data.chiriasNume || "________________________"}</span>
        </p>
        <p style={{ ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" }}>
          CNP: <span style={bold}>{data.chiriasCNP || "________________________"}</span>
        </p>
        <p style={{ ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" }}>
          CI seria <span style={bold}>{ciChiriasFmt}</span>
        </p>
        <p style={{ ...p, margin: "2px 0", paddingLeft: isMobile ? "12px" : "20px" }}>
          Domiciliul: <span style={bold}>{data.chiriasAdresa || "________________________"}</span>
        </p>
      </div>

      <hr style={{ border: "none", borderTop: "1px solid #e8e0d4", margin: "0 0 24px 0" }} />

      {/* Body intro */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "20px" }}>
        <p style={p}>
          Părțile au convenit modificarea Contractului de Închiriere nr. <span style={bold}>{data.numarContract || "___"}</span> din data de <span style={bold}>{fmtDateFromISO(data.dataContract)}</span>, privind imobilul situat la adresa: <span style={bold}>{data.adresaImobil || "________________________"}</span>, după cum urmează:
        </p>
      </div>

      {/* Dynamic articles */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "20px" }}>
        {articles.map((art) => (
          <div key={art.num} style={{ marginBottom: "16px" }}>
            <p style={p}>
              <span style={{ ...bold, fontFamily: "'Playfair Display', serif" }}>Art. {art.num}.</span>{" "}
              {art.text}
            </p>
          </div>
        ))}
      </div>

      {/* Closing */}
      <div style={{ pageBreakInside: "avoid", breakInside: "avoid", marginBottom: "20px" }}>
        <p style={p}>
          Prezentul act adițional s-a încheiat în 2 exemplare originale, câte unul pentru fiecare parte.
        </p>
      </div>

      {/* Signatures */}
      <div style={{
        marginTop: "48px",
        paddingTop: "32px",
        borderTop: "2px solid #e8e0d4",
        display: "flex",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: "32px",
        pageBreakInside: "avoid",
        breakInside: "avoid",
      }}>
        {/* Proprietar signature */}
        <div style={{ flex: "1 1 40%", minWidth: "200px" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            PROPRIETAR
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 0 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.proprietarNume || ""}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>

        {/* Chirias signature */}
        <div style={{ flex: "1 1 40%", minWidth: "200px" }}>
          <p style={{ fontSize: "13px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "0 0 4px 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>
            CHIRIAȘUL
          </p>
          <div style={{ borderBottom: "1px solid #d4cdc0", margin: "32px 0 8px" }} />
          <p style={{ fontSize: "14px", fontFamily: "'Georgia', serif", color: "#2d2418", margin: 0, fontWeight: 600 }}>
            {data.chiriasNume || ""}
          </p>
          <p style={{ fontSize: "12px", fontFamily: "'Georgia', serif", color: "#8a7e70", margin: "8px 0 0 0" }}>
            Data: {today}
          </p>
        </div>
      </div>
    </div>
  );
});

export default ActAditionalPreview;
